import { Button, useTheme } from '@material-ui/core'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  SettlementsInvoicesKPIsResponseModel,
  SettlementsStatusKPIsResponseModel
} from '../../../../../api/Argentia/endpoints/settlements/model/settlements'
import { envConfig } from '../../../../../config/envConfig'
import { capKpiValue } from '../../../../../helpers/numberHelper'
import {
  SettlementKpiBlock,
  SettlementKpiBlockRow
} from '../../../components/settlementKpiBlock/settlementKpiBlock'
import { SettlementInvoiceKpiType } from '../settlementInvoicesKpiRightContent/settlementInvoicesKpiRightContent'

interface SettlementsInvoicesKpiBlocksProps {
  /** Resumen de todas las liquidaciones de la ejecución por estado */
  statusKpis?: SettlementsStatusKPIsResponseModel
  isLoadingStatusKpis?: boolean
  kpis?: SettlementsInvoicesKPIsResponseModel
  isLoadingKpis?: boolean
  /** KPI actualmente seleccionado */
  activeKpi?: SettlementInvoiceKpiType | null
  onKpiClick?: (kpiType: SettlementInvoiceKpiType) => void
  /** Si no se informa (ejecución cerrada) no se muestra el botón de regenerar */
  onRegenerateClick?: () => void
  /** La ejecución está procesando: el bloque En curso muestra el seguimiento y sus acciones */
  isProcessing?: boolean
  /** Procesando pero el polling se detuvo por máximo de reintentos */
  isPaused?: boolean
  /** Terminó de procesar mientras se seguía en esta pantalla: invita a actualizar la información */
  isFinished?: boolean
  onCheckAgainClick?: () => void
  onViewProgressClick?: () => void
  onRefreshClick?: () => void
}

/** Validar facturas: la fase activa es "Liquidaciones facturadas" */
export const SettlementsInvoicesKpiBlocks: React.FC<SettlementsInvoicesKpiBlocksProps> = ({
  statusKpis,
  isLoadingStatusKpis = false,
  kpis,
  isLoadingKpis = false,
  activeKpi,
  onKpiClick,
  onRegenerateClick,
  isProcessing = false,
  isPaused = false,
  isFinished = false,
  onCheckAgainClick,
  onViewProgressClick,
  onRefreshClick
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'settlementsInvoices'])
  const theme = useTheme()

  const maxDisplayValue = envConfig.kpiConfig.maxDisplayValue
  const issuedSettlements = statusKpis ? (statusKpis.closed ?? 0) : undefined
  const billedSettlements = statusKpis
    ? (statusKpis.open ?? 0) +
      (issuedSettlements ?? 0) +
      (statusKpis.error ?? 0) +
      (statusKpis.cancelled ?? 0)
    : undefined
  // Las objeciones son un subconjunto de las no emitidas: se muestran excluyentes para que los totales cuadren
  const nonIssuedWithObjections = Math.min(
    kpis?.totalInvoicesWithObjections ?? 0,
    statusKpis?.open ?? 0
  )
  const nonIssuedWithoutObjections = (statusKpis?.open ?? 0) - nonIssuedWithObjections
  const isLoading = isLoadingStatusKpis || isLoadingKpis

  return (
    <>
      <SettlementKpiBlock
        isLoading={isLoadingStatusKpis}
        title={t('settlementsInvoices:components.kpiBlocks.unbilled.title')}
        value={capKpiValue(statusKpis?.pending, maxDisplayValue)}
        caption={t('settlementsInvoices:components.kpiBlocks.unbilled.caption')}
        footer={
          onRegenerateClick && (
            <Button variant="text" color="primary" onClick={onRegenerateClick}>
              {t('settlementsInvoices:components.kpiBlocks.unbilled.regenerate')}
            </Button>
          )
        }
      />
      <SettlementKpiBlock
        isLoading={isLoadingStatusKpis}
        title={t('settlementsInvoices:components.kpiBlocks.inProgress.title')}
        value={capKpiValue(statusKpis?.ongoing, maxDisplayValue)}
        caption={t('settlementsInvoices:components.kpiBlocks.inProgress.caption')}
        processingMessage={
          isProcessing || isFinished
            ? t(
                'settlementsInvoices:components.kpiBlocks.inProgress.' +
                  (isFinished ? 'finished' : isPaused ? 'paused' : 'processing')
              )
            : undefined
        }
        isProcessingPaused={isPaused || isFinished}
        footer={
          (isProcessing || isFinished) && (
            <>
              {isPaused
                ? onCheckAgainClick && (
                    <Button variant="text" color="primary" onClick={onCheckAgainClick}>
                      {t('settlementsInvoices:components.kpiBlocks.inProgress.checkAgain')}
                    </Button>
                  )
                : onRefreshClick && (
                    <Button variant="text" color="primary" onClick={onRefreshClick}>
                      {t('settlementsInvoices:components.kpiBlocks.inProgress.refresh')}
                    </Button>
                  )}
              {!isFinished && onViewProgressClick && (
                <Button variant="outlined" color="primary" onClick={onViewProgressClick}>
                  {t('settlementsInvoices:components.kpiBlocks.inProgress.viewProgress')}
                </Button>
              )}
            </>
          )
        }
      />
      <SettlementKpiBlock
        isActive
        isLoading={isLoadingStatusKpis}
        title={t('settlementsInvoices:components.kpiBlocks.billed.title')}
        value={capKpiValue(billedSettlements, maxDisplayValue)}
        caption={t('settlementsInvoices:components.kpiBlocks.billed.caption')}
        linkLabel={t('settlementsInvoices:components.kpiBlocks.billed.viewAll')}
        onLinkClick={onKpiClick ? () => onKpiClick('totalInvoicesGenerated') : undefined}
        isLinkSelected={activeKpi === 'totalInvoicesGenerated'}
      >
        <SettlementKpiBlockRow
          isLoading={isLoadingStatusKpis}
          label={t('settlementsInvoices:components.kpiBlocks.billed.issued')}
          value={capKpiValue(issuedSettlements, maxDisplayValue)}
          color={theme.palette.success.main}
          onClick={onKpiClick ? () => onKpiClick('issuedInvoices') : undefined}
          isSelected={activeKpi === 'issuedInvoices'}
        />
        <SettlementKpiBlockRow
          isLoading={isLoadingStatusKpis}
          label={t('settlementsInvoices:components.kpiBlocks.billed.nonIssued')}
          value={capKpiValue(statusKpis?.open, maxDisplayValue)}
          color={theme.palette.warning.main}
          onClick={onKpiClick ? () => onKpiClick('nonIssuedInvoices') : undefined}
          isSelected={activeKpi === 'nonIssuedInvoices'}
        >
          <SettlementKpiBlockRow
            isNested
            isLoading={isLoading}
            label={t('settlementsInvoices:components.kpiBlocks.billed.withoutObjections')}
            value={capKpiValue(nonIssuedWithoutObjections, maxDisplayValue)}
            onClick={onKpiClick ? () => onKpiClick('nonIssuedWithoutObjections') : undefined}
            isSelected={activeKpi === 'nonIssuedWithoutObjections'}
          />
          <SettlementKpiBlockRow
            isNested
            isLoading={isLoading}
            label={t('settlementsInvoices:components.kpiBlocks.billed.withObjections')}
            value={capKpiValue(nonIssuedWithObjections, maxDisplayValue)}
            color={theme.palette.warning.main}
            onClick={onKpiClick ? () => onKpiClick('nonIssuedWithObjections') : undefined}
            isSelected={activeKpi === 'nonIssuedWithObjections'}
          />
        </SettlementKpiBlockRow>
        <SettlementKpiBlockRow
          isLoading={isLoadingStatusKpis}
          label={t('settlementsInvoices:components.kpiBlocks.billed.error')}
          value={capKpiValue(statusKpis?.error, maxDisplayValue)}
          color={theme.palette.error.main}
          onClick={onKpiClick ? () => onKpiClick('errorSettlements') : undefined}
          isSelected={activeKpi === 'errorSettlements'}
        />
      </SettlementKpiBlock>
    </>
  )
}
